// src/components/atoms/OverlayPanel/OverlayPanelAtom.tsx
//
// Generic backdrop/dismiss/Esc-handling overlay primitive. Molecules and
// organisms may not import `primereact/*` directly (Rule 2) — this is the
// atom that lets any of them anchor a popover to a trigger without
// hand-rolling click-outside/Escape handling. Extracted for EAINT-781's
// `LevelFilterPopoverOrganism`; `TreeMapColumnFilter` (deleted in Phase 6)
// reached into `primereact/overlaypanel` directly, which is exactly the
// Rule 2 violation this atom exists to stop from recurring.

import React, { forwardRef } from 'react';
import { OverlayPanel, OverlayPanelProps } from 'primereact/overlaypanel';
import './OverlayPanelAtom.scss';

/** The event that opened the panel; PrimeReact reads the trigger off `currentTarget` when no `target` is given. */
export type OverlayPanelTriggerEvent = React.SyntheticEvent | undefined | null;
/** An explicit anchor for the panel, when the trigger is not the event's own element. */
export type OverlayPanelTarget = HTMLElement | EventTarget | null;

export interface OverlayPanelHandle {
  toggle(event: OverlayPanelTriggerEvent, target?: OverlayPanelTarget): void;
  show(event: OverlayPanelTriggerEvent, target?: OverlayPanelTarget): void;
  hide(): void;
  isVisible(): boolean;
}

export interface OverlayPanelAtomProps extends Omit<OverlayPanelProps, 'className'> {
  className?: string;
}

const OverlayPanelAtom = forwardRef<OverlayPanelHandle, OverlayPanelAtomProps>((props, ref) => {
  const { className = '', children, id: idProp, onHide, ...rest } = props;
  const internalRef = React.useRef<OverlayPanel>(null);
  // The trigger↔panel link, as WAI-ARIA disclosure spells it: the trigger carries
  // `aria-controls` naming the panel and `aria-expanded` for its state. It is also what lets
  // the parity gate tell a picker anchored INSIDE a popover (nested, fine) from two unrelated
  // overlays overlapping (GEO-2): the panel is portaled to `document.body`, so nothing in
  // the DOM tree says where it belongs — this attribute does.
  const generatedId = React.useId().replaceAll(':', '');
  const panelId = idProp ?? `overlay-panel-${generatedId}`;
  const triggerRef = React.useRef<HTMLElement | null>(null);

  const markTrigger = (event: OverlayPanelTriggerEvent, target?: OverlayPanelTarget) => {
    const el = (target ?? event?.currentTarget) as HTMLElement | null | undefined;
    if (!el || typeof el.setAttribute !== 'function') return;
    triggerRef.current = el;
    el.setAttribute('aria-controls', panelId);
    el.setAttribute('aria-expanded', 'true');
  };
  const unmarkTrigger = () => {
    triggerRef.current?.setAttribute('aria-expanded', 'false');
  };

  React.useImperativeHandle(ref, () => ({
    toggle: (event, target) => {
      const opening = !(internalRef.current?.isVisible() ?? false);
      if (opening) markTrigger(event, target); else unmarkTrigger();
      internalRef.current?.toggle(event ?? null, target ?? undefined);
    },
    show: (event, target) => {
      markTrigger(event, target);
      internalRef.current?.show(event ?? null, target ?? undefined);
    },
    hide: () => {
      unmarkTrigger();
      internalRef.current?.hide();
    },
    isVisible: () => internalRef.current?.isVisible() ?? false,
  }));

  return (
    <OverlayPanel
      ref={internalRef}
      id={panelId}
      className={`overlay-panel-atom ${className}`.trim()}
      onHide={() => {
        unmarkTrigger(); // dismissable click-outside / Escape close it without going through `hide()`
        onHide?.();
      }}
      {...rest}
    >
      {children}
    </OverlayPanel>
  );
});

OverlayPanelAtom.displayName = 'OverlayPanelAtom';

export default OverlayPanelAtom;
